import { deepmerge } from 'deepmerge-ts'
import {
  curry as _curry,
  flatten as _flatten,
  flattenDeep as _flattenDeep,
  flow as _flow,
  isEmpty as _isEmpty,
  last as _last,
} from 'lodash/fp'

import { ButtonVariant, MessageType } from '@flowx/angular-ui-toolkit'
import { InputType } from '@flowx/core-sdk'
import { DataModelAttributeType } from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { Platform } from '@flowx/designer/modules/theme/model/platform.model'
import { getFormFieldDataModelType } from '@flowx/designer/modules/ui-designer/services/form-field-data-type.util'
import {
  ArrayFormFields,
  TemplateConfigEntity,
} from '@flowx/designer/modules/ui-designer/services/templateConfig.entity'
import { CreateDataModelAttributeParams } from '@flowx/designer/shared/entities/data-model.entity'
import { Area } from '@flowx/designer/shared/entities/nav-area.entity'
import {
  ComponentIdentifierEnum,
  DisplayOptionsInterface,
  FlxValidator,
  FormFieldsEnum,
  NomenclatorValue,
  TemplateConfigInterface,
  TemplateConfigSaveStatus,
  TemplateConfigValidationStatus,
} from '@flowx/shared/interfaces'
import {
  DimensionTypes,
  FlexLayoutAlignType,
  FlexLayoutType,
  HeightType,
  WidthType,
} from '@flowx/ui-theme'

import { ProjectDataModelCatalogueEntry } from '../../../conversation/project-data-model-catalogue'
import {
  AiDesignerContext,
  DataModelDraft,
  DataModelDraftItem,
  DataModelDraftUpdateItem,
  TableElementsWithDataModelTextInterpolation,
} from '../../../types/ai.types'
import { DataModelItemStatus } from '../dictionary/designer.dictionary'

// --- Types ---

export type BulkUpdatePayload = Omit<
  TemplateConfigInterface,
  'id' | 'flowxUuid' | 'templateConfig'
> & {
  templateConfigWithData: BulkUpdatePayload[]
  dataModelProperty: {
    name: string
    description: string
    type: DataModelAttributeType
    exampleValue: string
    useInReporting: boolean
    isSensitive: boolean
    isArray: boolean
  }
}

export type AiTemplateConfig = {
  interface: AiUiInterface
  components: AiUiTemplateElement[]
}

export type AiUiTemplateElement = {
  id: number
  pId: number
  ci: string
  lt: string
  dir: string
  g: number
  cl: number
  al: string
  js: string
  wp: string
  gaph: number
  gapv: number
  cspan: number
  pd: string
  mg: string
  wt: string
  ht: string
  chaxis: string
  cvaxis: string
  atp: string
  lb: string
  ex: string
  st: string
  k: string
  w: number
  h: number
  ds: string
  dt: string
  bgCol: string
  fgCol: string
  // v8 schema-extension fields (optional; emitted by the agent for behavior/validation/rules)
  validators?: AiValidator[]
  props?: AiComponentProps
  disableRules?: string
  hideRules?: string
  // "show-when" expression — the renderer has no `visible` prop, so it maps to a negated `hide`.
  visibleRules?: string
}

// Validation rule as emitted by the agent: a `type` plus an optional message and a
// constraint carried either in `value` or in a type-named key (pattern/min/max/…).
export type AiValidator = {
  type: string
  message?: string
  value?: unknown
  pattern?: string
  min?: number
  max?: number
  minLength?: number
  maxLength?: number
}

// Component-specific settings as emitted by the agent. Only a subset maps to renderable
// FE props today (clearable/searchable/required); the rest are accepted but not yet mapped.
export type AiComponentProps = {
  clearable?: boolean
  searchable?: boolean
  required?: boolean
  readOnly?: boolean
  min?: number
  max?: number
  step?: number
  format?: string
  minDate?: string
  maxDate?: string
  mask?: string
  tooltip?: string
  icon?: string
  iconPosition?: string
  defaultValue?: string
  acceptedTypes?: string
  maxSize?: number
  maxCount?: number
  accordion?: boolean
}

export enum UiFlowLinkButtonType {
  Submit = 'submit',
  Cancel = 'cancel',
}

export const AI_PLATFORM_CODE = { Web: 'WB', Mobile: 'MB' } as const
export type AiPlatformCode = (typeof AI_PLATFORM_CODE)[keyof typeof AI_PLATFORM_CODE]

// `st` packs two values per component — a text's `colour//typography`, an image's `width//height`.
const AI_ST_SEPARATOR = '//'
export const packAiStyleValue = (first: string, second: string): string =>
  `${first}${AI_ST_SEPARATOR}${second}`
export const unpackAiStyleValue = (st: string | undefined): [string, string] => {
  const [first = '', second = ''] = (st ?? '').split(AI_ST_SEPARATOR)
  return [first, second]
}

export type AiUiInterface = {
  it: string
  ty: AiPlatformCode
  ds: string
}

export type AiLocationUI = {
  ci: number
  index: number
  prompt: string
}

export enum AiComponentDataType {
  N = DataModelAttributeType.INTEGER,
  S = DataModelAttributeType.STRING,
  B = DataModelAttributeType.BOOLEAN,
  D = DataModelAttributeType.DATE_TIME,
  C = DataModelAttributeType.CURRENCY,
  O = DataModelAttributeType.OBJECT,
  NONE = '',
}

export const AiInputTypeToDataTypeMap: Record<InputType, AiComponentDataType> = {
  [InputType.TEXT]: AiComponentDataType.S,
  [InputType.PASSWORD]: AiComponentDataType.S,
  [InputType.EMAIL]: AiComponentDataType.S,
  [InputType.NUMBER]: AiComponentDataType.N,
  // TODO: Check what value we should have here
  [InputType.CODE]: null,
}

export const AiComponent = {
  TEXT: 'TX',
  LINK: 'LK',
  INPUT: 'IN',
  DATEPICKER: 'DT',
  TEXTAREA: 'TA',
  RADIO: 'RD',
  SELECT: 'SL',
  MULTI_SELECT: 'MS',
  SWITCH: 'SW',
  BUTTON: 'BT',
  CHECKBOX: 'CK',
  IMAGE: 'IM',
  SLIDER: 'SD',
  SEGMENTED_BUTTON: 'SBT',
  FILE_UPLOAD: 'FU',
  BULK_FILE_UPLOAD: 'MFU',
  DOCUMENT_PREVIEW: 'FP',
  MESSAGE: 'MSG',
  TABLE: 'TB',
  TABLE_CELL: 'TC',
  CARD: 'CD',
  FORM: 'FM',
  CONTAINER: 'CN',
  COLLECTION: 'CL',
  // Navigation-area codes from the agent's NavigationComponent enum (TB/TC taken, hence TBB).
  PAGE: 'PG',
  TAB_BAR: 'TBB',
  TAB: 'TAB',
  ZONE: 'ZN',
  MODAL: 'MD',
} as const

export type AiComponentIdentifier = (typeof AiComponent)[keyof typeof AiComponent]

const AiComponentIdentifierToComponentIdentifierEnum: Record<
  AiComponentIdentifier,
  ComponentIdentifierEnum | FormFieldsEnum
> = {
  [AiComponent.TEXT]: ComponentIdentifierEnum.TEXT,
  [AiComponent.LINK]: ComponentIdentifierEnum.LINK,
  [AiComponent.INPUT]: FormFieldsEnum.INPUT,
  [AiComponent.DATEPICKER]: FormFieldsEnum.DATEPICKER,
  [AiComponent.TEXTAREA]: FormFieldsEnum.TEXTAREA,
  [AiComponent.RADIO]: FormFieldsEnum.RADIO,
  [AiComponent.SELECT]: FormFieldsEnum.SELECT,
  [AiComponent.MULTI_SELECT]: FormFieldsEnum.MULTI_SELECT,
  [AiComponent.SWITCH]: FormFieldsEnum.SWITCH,
  [AiComponent.BUTTON]: ComponentIdentifierEnum.BUTTON,
  [AiComponent.CHECKBOX]: FormFieldsEnum.CHECKBOX,
  [AiComponent.IMAGE]: ComponentIdentifierEnum.IMAGE,
  [AiComponent.SLIDER]: FormFieldsEnum.SLIDER,
  [AiComponent.SEGMENTED_BUTTON]: FormFieldsEnum.SEGMENTED_BUTTON,
  [AiComponent.FILE_UPLOAD]: ComponentIdentifierEnum.FILE_UPLOAD,
  [AiComponent.BULK_FILE_UPLOAD]: ComponentIdentifierEnum.BULK_FILE_UPLOAD,
  [AiComponent.DOCUMENT_PREVIEW]: ComponentIdentifierEnum.DOCUMENT_PREVIEW,
  [AiComponent.MESSAGE]: ComponentIdentifierEnum.MESSAGE,
  [AiComponent.TABLE]: ComponentIdentifierEnum.TABLE,
  [AiComponent.TABLE_CELL]: ComponentIdentifierEnum.TABLE_CELL,
  [AiComponent.CARD]: ComponentIdentifierEnum.CARD,
  [AiComponent.FORM]: ComponentIdentifierEnum.FORM,
  [AiComponent.CONTAINER]: ComponentIdentifierEnum.CONTAINER,
  [AiComponent.COLLECTION]: ComponentIdentifierEnum.COLLECTION,
  [AiComponent.PAGE]: ComponentIdentifierEnum.PAGE,
  [AiComponent.TAB_BAR]: ComponentIdentifierEnum.TAB_BAR,
  [AiComponent.TAB]: ComponentIdentifierEnum.TAB,
  [AiComponent.ZONE]: ComponentIdentifierEnum.ZONE,
  [AiComponent.MODAL]: ComponentIdentifierEnum.MODAL,
}

const AI_FORM_FIELDS = new Set<AiComponentIdentifier>([
  AiComponent.INPUT,
  AiComponent.SELECT,
  AiComponent.MULTI_SELECT,
  AiComponent.DATEPICKER,
  AiComponent.CHECKBOX,
  AiComponent.SEGMENTED_BUTTON,
  AiComponent.RADIO,
  AiComponent.TEXTAREA,
  AiComponent.SWITCH,
  AiComponent.SLIDER,
])

// --- Helpers ---

const HEADING_TOKENS = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6']
const PARAGRAPH_TOKENS = ['P1', 'P2', 'P3']

const extractTextStyle = (textStyle: string): string | null => {
  const [color, token] = unpackAiStyleValue(textStyle)
  if (!token) {
    return null
  }
  if (HEADING_TOKENS.includes(token)) {
    return `typography@Heading/${token}#${color}`
  }
  if (PARAGRAPH_TOKENS.includes(token)) {
    return `typography@Paragraph/${token}#${color}`
  }
  return null
}

const extractButtonType = (buttonStyle: string): ButtonVariant => {
  if (!buttonStyle) {
    return 'text'
  }

  if (buttonStyle === 'filled') {
    return 'fill'
  } else {
    return buttonStyle as ButtonVariant
  }
}

const isTechnicalContent = (text: string): boolean => {
  const lower = text.toLowerCase()
  return (
    lower.includes('aria-') ||
    lower.includes('role=') ||
    lower.includes('onclick') ||
    lower.includes('tabindex') ||
    lower.includes('focuson') ||
    lower.includes('shortcut=') ||
    lower.includes('pattern=') ||
    lower.includes('type=submit') ||
    lower.includes('id=#') ||
    /max\s+\d+\s+characters/i.test(text) ||
    /aria-describedby/i.test(text)
  )
}

const isImplementationNote = (text: string): boolean => {
  const lower = text.toLowerCase()
  return (
    isTechnicalContent(text) ||
    lower.includes('accessibility') ||
    lower.includes('implementation') ||
    lower.includes('server-side') ||
    lower.includes('validation error') ||
    lower.includes('validation message') ||
    lower.includes('error message area') ||
    lower.includes('dynamically populated') ||
    lower.includes('placeholder for') ||
    lower.includes('will appear here')
  )
}

const isDataModelPath = (text: string): boolean =>
  /^[a-zA-Z_]\w*(\.[a-zA-Z_]\w*)+$/.test(text.trim())

const parseDataSourceValues = (
  label: string
): Pick<NomenclatorValue, 'order' | 'label' | 'value'>[] =>
  label && label.search('//') > -1
    ? label.split('//').map((value, index) => ({ order: index, label: value, value }))
    : [{ order: 0, label, value: label || '' }]

const parseCollectionChildren = (
  ex: string,
  parentKey: string,
  withIdsAndValidationStatus: WithIdsAndValidationStatus
): TemplateConfigInterface[] => {
  if (!ex || ex.indexOf('//') === -1) {
    return []
  }
  return ex.split('//').map((entry) => {
    const [typeCode, label] = entry.includes(':') ? entry.split(':', 2) : [AiComponent.INPUT, entry]
    const componentIdentifier =
      AiComponentIdentifierToComponentIdentifierEnum[typeCode as AiComponentIdentifier] ||
      FormFieldsEnum.INPUT
    const childKey = label
      ? label
          .trim()
          .replace(/\s+(.)/g, (_, c) => c.toUpperCase())
          .replace(/^\w/, (c) => c.toLowerCase())
      : ''
    return _flow(
      withIdsAndValidationStatus,
      TemplateConfigEntity.withLabel(label?.trim() || ''),
      ...(childKey ? [TemplateConfigEntity.withKey(`${parentKey}.${childKey}`)] : [])
    )(
      TemplateConfigEntity.createDefaultTemplateConfig(componentIdentifier)
    ) as TemplateConfigInterface
  })
}

type WithIdsAndValidationStatus = (
  templateConfig: Partial<TemplateConfigInterface>
) => Partial<TemplateConfigInterface>

// --- Core Mapper ---

const decorateTemplateConfigTreeWithOrder = (
  order = 0,
  templateConfig: TemplateConfigInterface
): TemplateConfigInterface => ({
  ...templateConfig,
  order,
  ...(templateConfig.templateConfig
    ? {
        templateConfig: templateConfig.templateConfig.map((child, index) =>
          decorateTemplateConfigTreeWithOrder(index, child)
        ),
      }
    : {}),
})

// Navigation areas name themselves through flowxProps.title (what the runtime reads), not the
// component label — whether they are the generated subtree's root or an element inside it.
const isNavigationAreaIdentifier = (identifier: ComponentIdentifierEnum | FormFieldsEnum): boolean =>
  Area.isNavigationArea(Area.fromComponentIdentifierToNavigationArea(identifier))

export const mapAiUiToTemplateConfig = (
  idGenerator: () => number,
  flowxUuidGenerator: () => string,
  aiTemplateConfig: AiTemplateConfig
): Partial<TemplateConfigInterface> => {
  const withIdsAndValidationStatus: WithIdsAndValidationStatus = _flow(
    TemplateConfigEntity.withId(idGenerator),
    TemplateConfigEntity.withFlowxUuid(flowxUuidGenerator),
    TemplateConfigEntity.withMeta({
      validationStatus: TemplateConfigValidationStatus.VALID,
      saveStatus: TemplateConfigSaveStatus.DRAFT,
    })
  )
  if (!aiTemplateConfig.components) {
    return {}
  }
  const parentComponent = aiTemplateConfig.components.find((component) => component.pId === 0)
  if (!parentComponent) {
    return {}
  }

  const parentIdentifier = AiComponentIdentifierToComponentIdentifierEnum[parentComponent.ci]
  if (!parentIdentifier) {
    return {}
  }

  const isRootCard = parentIdentifier === ComponentIdentifierEnum.CARD
  const rootPadding = parentComponent.pd?.split(' ').map(Number)
  const rootMargin = parentComponent.mg?.split(' ').map(Number)

  const buildSteps = [
    withIdsAndValidationStatus,
    ...(parentComponent.lt || parentComponent.cl
      ? [
          parentComponent.lt === 'linear'
            ? TemplateConfigEntity.withFlexLayout(Platform.Web)({
                fxLayout: (parentComponent.dir +
                  (parentComponent.wp === 'wrap' ? ' wrap' : '')) as FlexLayoutType,
                fxLayoutAlign: (parentComponent.js +
                  ' ' +
                  parentComponent.al) as FlexLayoutAlignType,
                fxLayoutGap: parentComponent.gaph || parentComponent.gapv,
              })
            : TemplateConfigEntity.withGridLayout(Platform.Web)({
                columns: parentComponent.cl,
                columnWidths: Array.from({ length: parentComponent.cl }, () => ({
                  width: 1,
                  unit: 'fr',
                })),
                columnGap: parentComponent.gaph,
                rowGap: parentComponent.gapv,
              }),
        ]
      : []),
    ...(isRootCard && parentComponent.lb
      ? [
          TemplateConfigEntity.withCardTitleAndSubtitle(
            Platform.Web,
            parentComponent.lb,
            parentComponent.ds || ''
          ),
        ]
      : []),
    ...(rootPadding?.length === 4
      ? [
          TemplateConfigEntity.withPadding(
            rootPadding[0] !== 0 ? rootPadding[0] : undefined,
            rootPadding[1] !== 0 ? rootPadding[1] : undefined,
            rootPadding[2] !== 0 ? rootPadding[2] : undefined,
            rootPadding[3] !== 0 ? rootPadding[3] : undefined
          ),
        ]
      : []),
    ...(rootMargin?.length === 4
      ? [
          TemplateConfigEntity.withMargin(
            rootMargin[0] !== 0 ? rootMargin[0] : undefined,
            rootMargin[1] !== 0 ? rootMargin[1] : undefined,
            rootMargin[2] !== 0 ? rootMargin[2] : undefined,
            rootMargin[3] !== 0 ? rootMargin[3] : undefined
          ),
        ]
      : []),
    ...(parentComponent.k ? [TemplateConfigEntity.withKey(`${parentComponent.k}`)] : []),
    ...(isNavigationAreaIdentifier(parentIdentifier as ComponentIdentifierEnum) &&
    parentComponent.lb
      ? [TemplateConfigEntity.withCardTitle(parentComponent.lb)]
      : []),
  ]

  const interfaceTC: Partial<TemplateConfigInterface> = _flow(buildSteps)(
    TemplateConfigEntity.createDefaultTemplateConfig(parentIdentifier)
  )

  const children = buildComponentTree(
    aiTemplateConfig.components,
    parentComponent.id,
    mapAiElementComponentToTemplateConfig,
    withIdsAndValidationStatus
  )

  const interfaceTCWithChildren = {
    ...interfaceTC,
    templateConfig: children,
  }
  return decorateTemplateConfigTreeWithOrder(0, interfaceTCWithChildren as TemplateConfigInterface)
}

// --- v8 schema-extension mapping (validators / props / hide+disable expressions) ---

// Agent validator `type` → FE validator name (see DefaultValidators in dnd-constants).
const AI_VALIDATOR_NAME: Record<string, string> = {
  required: 'required',
  email: 'email',
  pattern: 'pattern',
  regex: 'pattern',
  minlength: 'minlength',
  minLength: 'minlength',
  maxlength: 'maxlength',
  maxLength: 'maxlength',
  min: 'min',
  max: 'max',
}

const VALIDATOR_DEFAULT_MESSAGE: Record<string, string> = {
  required: 'This field is required',
  email: 'Enter a valid email',
  pattern: 'Invalid format',
  minlength: 'Value is too short',
  maxlength: 'Value is too long',
  min: 'Value is too small',
  max: 'Value is too large',
}

// Where a validator's constraint lives in the agent's schema, by the FE rule name it maps to. The
// constraint may also arrive in `value`. The UI flow serializer reads the same table backwards.
export const VALIDATOR_PARAM_FIELD = {
  pattern: 'pattern',
  minlength: 'minLength',
  maxlength: 'maxLength',
  min: 'min',
  max: 'max',
} as const satisfies Record<string, keyof AiValidator>

const validatorParam = (name: string, validator: AiValidator): unknown =>
  name in VALIDATOR_PARAM_FIELD
    ? (validator[VALIDATOR_PARAM_FIELD[name as keyof typeof VALIDATOR_PARAM_FIELD]] ??
      validator.value)
    : undefined

const mapAiValidatorToFlx = (validator: AiValidator): FlxValidator | null => {
  const name = AI_VALIDATOR_NAME[validator.type]
  if (!name) {
    return null
  }
  const paramValue = validatorParam(name, validator)
  return {
    name,
    message: validator.message || VALIDATOR_DEFAULT_MESSAGE[name] || '',
    isCustom: false,
    type: 'sync',
    ...(paramValue != null ? { params: [paramValue] } : {}),
  }
}

// Agent validators (plus a `required` prop) → FE FlxValidator[] (top-level `validators`).
const mapAiValidators = (aiElement: AiUiTemplateElement): FlxValidator[] => {
  const fromValidators = (aiElement.validators ?? [])
    .map(mapAiValidatorToFlx)
    .filter((v): v is FlxValidator => v !== null)
  const requiredFromProps =
    aiElement.props?.required && !fromValidators.some((v) => v.name === 'required')
      ? [mapAiValidatorToFlx({ type: 'required' })].filter((v): v is FlxValidator => v !== null)
      : []
  return [...requiredFromProps, ...fromValidators]
}

// Agent hide/disable expressions + clearable/searchable props → generic-platform flowxProps.
const mapAiFlowxPropsPatch = (
  aiElement: AiUiTemplateElement,
  componentIdentifier: ComponentIdentifierEnum | FormFieldsEnum
): Record<string, unknown> => {
  const props = aiElement.props ?? {}
  const isSelectLike =
    componentIdentifier === FormFieldsEnum.SELECT ||
    componentIdentifier === FormFieldsEnum.MULTI_SELECT
  // hide expression: prefer the negative `hideRules`; tolerate the agent's positive
  // `visibleRules` ("show-when X") by negating it, since the renderer only has `hide`.
  const hide = aiElement.hideRules
    ? aiElement.hideRules
    : aiElement.visibleRules
      ? `!(${aiElement.visibleRules})`
      : undefined
  return {
    ...(hide ? { hide } : {}),
    ...(aiElement.disableRules ? { disabled: aiElement.disableRules } : {}),
    ...(props.clearable != null ? { hasClear: props.clearable } : {}),
    ...(props.searchable != null && isSelectLike ? { hasSearch: props.searchable } : {}),
  }
}

// Immutably set top-level validators (no-op when there are none).
const withAiValidators =
  (validators: FlxValidator[]) =>
  (tc: Partial<TemplateConfigInterface>): Partial<TemplateConfigInterface> =>
    validators.length ? { ...tc, validators } : tc

// Immutably patch the generic-platform flowxProps (where the config panel reads behavior props).
const withGenericFlowxProps =
  (patch: Record<string, unknown>) =>
  (tc: Partial<TemplateConfigInterface>): Partial<TemplateConfigInterface> =>
    Object.keys(patch).length === 0
      ? tc
      : {
          ...tc,
          platformsDisplayOptions: tc.platformsDisplayOptions?.map((p) =>
            p.platform === 'generic'
              ? ({ ...p, flowxProps: { ...p.flowxProps, ...patch } } as DisplayOptionsInterface)
              : p
          ),
        }

const mapAiElementComponentToTemplateConfig = (
  aiElement: AiUiTemplateElement,
  withIdsAndValidationStatus: WithIdsAndValidationStatus
): TemplateConfigInterface => {
  const componentIdentifier = AiComponentIdentifierToComponentIdentifierEnum[aiElement.ci]
  const isButton = componentIdentifier === ComponentIdentifierEnum.BUTTON
  const isImage = componentIdentifier === ComponentIdentifierEnum.IMAGE
  const isInput = componentIdentifier === FormFieldsEnum.INPUT
  const isText = componentIdentifier === ComponentIdentifierEnum.TEXT
  const isLink = componentIdentifier === ComponentIdentifierEnum.LINK
  const isMessage = componentIdentifier === ComponentIdentifierEnum.MESSAGE
  const isCheckbox = componentIdentifier === FormFieldsEnum.CHECKBOX
  const isRadio = componentIdentifier === FormFieldsEnum.RADIO
  const isContainer = componentIdentifier === ComponentIdentifierEnum.CONTAINER
  const isForm = componentIdentifier === ComponentIdentifierEnum.FORM
  const isCard = componentIdentifier === ComponentIdentifierEnum.CARD
  const isTable = componentIdentifier === ComponentIdentifierEnum.TABLE
  const isNavigationArea = isNavigationAreaIdentifier(componentIdentifier)
  const hasLabel = !isImage

  const elementsWithDataSource = new Set<FormFieldsEnum>([
    FormFieldsEnum.RADIO,
    FormFieldsEnum.SELECT,
    FormFieldsEnum.MULTI_SELECT,
    FormFieldsEnum.CHECKBOX,
    FormFieldsEnum.SEGMENTED_BUTTON,
  ])

  if (!aiElement.ci || !componentIdentifier) {
    return _flow(withIdsAndValidationStatus)(
      TemplateConfigEntity.createDefaultTemplateConfig(ComponentIdentifierEnum.CONTAINER)
    ) as TemplateConfigInterface
  }

  let changeForCheckbox = false
  let checkboxLabel = false
  if (aiElement.ci === AiComponent.CHECKBOX && aiElement.ex === '' && aiElement.lb !== '') {
    changeForCheckbox = true
  }
  if (
    (aiElement.ci === AiComponent.CHECKBOX || aiElement.ci === AiComponent.RADIO) &&
    aiElement.lb !== '' &&
    aiElement.ex !== '' &&
    aiElement.ex.trim() === aiElement.lb.trim()
  ) {
    checkboxLabel = true
  }

  const dataSourceValues = parseDataSourceValues(changeForCheckbox ? aiElement.lb : aiElement.ex)
  const hasDataSource =
    elementsWithDataSource.has(componentIdentifier as FormFieldsEnum) && dataSourceValues.length > 0

  const isFormField =
    componentIdentifier === FormFieldsEnum.INPUT ||
    componentIdentifier === FormFieldsEnum.SELECT ||
    componentIdentifier === FormFieldsEnum.CHECKBOX ||
    componentIdentifier === FormFieldsEnum.RADIO ||
    componentIdentifier === FormFieldsEnum.SEGMENTED_BUTTON

  const textStyle = extractTextStyle(aiElement.st)
  const buttonType = extractButtonType(aiElement.st)
  const hasLabelStyle = (isText || isLink) && !!textStyle

  const validateSpacing = (value: string | undefined): number[] | undefined => {
    if (!value) {
      return undefined
    }
    const values = value.split(' ')
    if (values.length !== 4) {
      return undefined
    }
    return values.map(Number)
  }

  const padding = validateSpacing(aiElement.pd)
  const margin = validateSpacing(aiElement.mg)

  // v8 schema-extension: validators, behavior props (clear/search) and hide/disable expressions
  const aiValidators = mapAiValidators(aiElement)
  const flowxPropsPatch = mapAiFlowxPropsPatch(aiElement, componentIdentifier)

  const buildSteps = [
    withIdsAndValidationStatus,
    ...(aiElement.cspan > 1
      ? [
          TemplateConfigEntity.withGridChildLayout(Platform.Web, {
            colSpan: aiElement.cspan,
          }),
        ]
      : []),
    ...(isMessage ? [TemplateConfigEntity.withMessageType(aiElement.st as MessageType)] : []),
    ...(aiElement.lt === 'linear' && (isCard || isForm || isContainer)
      ? [
          TemplateConfigEntity.withFlexLayout(Platform.Web)({
            fxLayout: (aiElement.dir + (aiElement.wp === 'wrap' ? ' wrap' : '')) as FlexLayoutType,
            fxLayoutAlign: (aiElement.js + ' ' + aiElement.al) as FlexLayoutAlignType,
            fxLayoutGap: aiElement.gapv || aiElement.gaph,
          }),
        ]
      : []),
    ...(aiElement.lt === 'grid' && (isCard || isForm || isContainer)
      ? [
          TemplateConfigEntity.withGridLayout(Platform.Web)({
            columns: aiElement.cl,
            columnWidths: Array.from({ length: aiElement.cl }, () => ({
              width: 1,
              unit: 'fr',
            })),
            columnGap: aiElement.gaph,
            rowGap: aiElement.gapv,
          }),
        ]
      : []),
    ...(aiElement.pd && padding
      ? [
          TemplateConfigEntity.withPadding(
            padding[0] !== 0 ? padding[0] : undefined,
            padding[1] !== 0 ? padding[1] : undefined,
            padding[2] !== 0 ? padding[2] : undefined,
            padding[3] !== 0 ? padding[3] : undefined
          ),
        ]
      : []),
    ...(aiElement.mg && margin
      ? [
          TemplateConfigEntity.withMargin(
            margin[0] !== 0 ? margin[0] : undefined,
            margin[1] !== 0 ? margin[1] : undefined,
            margin[2] !== 0 ? margin[2] : undefined,
            margin[3] !== 0 ? margin[3] : undefined
          ),
        ]
      : []),
    ...(aiElement.wt
      ? [
          TemplateConfigEntity.withWidthTypeFixed(
            Platform.Web,
            aiElement.wt as WidthType,
            aiElement.w
          ),
        ]
      : []),
    ...(aiElement.ht
      ? [TemplateConfigEntity.withHeightType(Platform.Web, aiElement.ht as HeightType, aiElement.h)]
      : []),
    ...(isImage
      ? [
          TemplateConfigEntity.withFixedSize(Platform.Web, {
            width: parseInt(unpackAiStyleValue(aiElement.st)[0], 10),
            height: parseInt(unpackAiStyleValue(aiElement.st)[1], 10),
          }),
        ]
      : []),
    ...(isImage && aiElement?.ex?.startsWith('data:')
      ? [
          TemplateConfigEntity.withImageBase64Source(aiElement.ex),
          TemplateConfigEntity.withHeightType(Platform.Web, DimensionTypes.Auto, 0),
        ]
      : [TemplateConfigEntity.withImageUrlSource('')]),
    ...(hasLabel
      ? [TemplateConfigEntity.withLabel(changeForCheckbox ? aiElement.ex : aiElement.lb)]
      : []),
    ...(isNavigationArea && aiElement.lb
      ? [TemplateConfigEntity.withCardTitle(aiElement.lb)]
      : []),
    ...(checkboxLabel ? [TemplateConfigEntity.withLabel('')] : []),
    ...(isFormField &&
    (isInput || componentIdentifier === FormFieldsEnum.TEXTAREA) &&
    aiElement.ex &&
    !isDataModelPath(aiElement.ex)
      ? [TemplateConfigEntity.withPlaceholder(aiElement.ex)]
      : []),
    ...(hasLabelStyle ? [TemplateConfigEntity.withLabelStyle(Platform.Web, textStyle)] : []),
    ...(isButton ? [TemplateConfigEntity.withButtonType(buttonType)] : []),
    ...(hasDataSource ? [TemplateConfigEntity.withStaticDataSource(dataSourceValues)] : []),
    ...(isInput ? [TemplateConfigEntity.withInputType(aiElement.st as InputType)] : []),
    ...(aiElement.k ? [TemplateConfigEntity.withKey(`${aiElement.k}`)] : []),
    ...(isTable && aiElement.k ? [TemplateConfigEntity.withTableSource(`${aiElement.k}`)] : []),
    ...(aiElement.bgCol && aiElement.bgCol !== 'no_color' && aiElement.bgCol !== aiElement.fgCol
      ? [TemplateConfigEntity.withBgColor(Platform.Web, aiElement.bgCol)]
      : []),
    ...(aiElement.fgCol && aiElement.fgCol !== 'no_color'
      ? [TemplateConfigEntity.withTxtColor(Platform.Web, aiElement.fgCol)]
      : []),
    ...(isCard
      ? [
          TemplateConfigEntity.withCardTitleAndSubtitle(
            Platform.Web,
            aiElement.lb,
            aiElement.ds || ''
          ),
        ]
      : []),
    ...(isForm ? [TemplateConfigEntity.withCardTitleAndSubtitle(Platform.Web, '', '')] : []),
    ...(isCheckbox || isRadio ? [TemplateConfigEntity.withClearCheckbox()] : []),
    // v8 schema-extension: validators, behavior props (clear/search) and hide/disable expressions
    withAiValidators(aiValidators),
    withGenericFlowxProps(flowxPropsPatch),
  ]

  return _flow(buildSteps)(
    TemplateConfigEntity.createDefaultTemplateConfig(componentIdentifier)
  ) as TemplateConfigInterface
}

const buildComponentTree = (
  components: AiUiTemplateElement[],
  parentId: number,
  mapFn: (
    c: AiUiTemplateElement,
    withIdsAndValidationStatus: WithIdsAndValidationStatus
  ) => TemplateConfigInterface,
  withIdsAndValidationStatus: WithIdsAndValidationStatus
): TemplateConfigInterface[] =>
  components
    .filter((component) => component.pId === parentId)
    .filter(
      (component) =>
        !(component.ci === AiComponent.TEXT && component.lb && isImplementationNote(component.lb))
    )
    .map((component) => {
      const node = mapFn(component, withIdsAndValidationStatus)
      const children = buildComponentTree(
        components,
        component.id,
        mapFn,
        withIdsAndValidationStatus
      )
      if (component.ci === AiComponent.TABLE) {
        const rowComponent = _flow(withIdsAndValidationStatus)(
          TemplateConfigEntity.createDefaultTemplateConfig(ComponentIdentifierEnum.TABLE_ROW)
        )
        return {
          ...node,
          templateConfig: [
            {
              ...rowComponent,
              templateConfig: children,
            },
          ],
        }
      } else if (component.ci === AiComponent.COLLECTION) {
        const prototypeComponent = _flow(withIdsAndValidationStatus)(
          TemplateConfigEntity.createDefaultTemplateConfig(
            ComponentIdentifierEnum.COLLECTION_PROTOTYPE
          )
        )
        const collectionChildren =
          children.length > 0
            ? children
            : parseCollectionChildren(component.ex, component.k, withIdsAndValidationStatus)
        return {
          ...node,
          templateConfig: [
            {
              ...prototypeComponent,
              templateConfig: collectionChildren,
            },
          ],
        }
      } else if (component.ci === AiComponent.TABLE_CELL) {
        const cellContent =
          component.k && component.ex
            ? _flow(
                withIdsAndValidationStatus,
                TemplateConfigEntity.withLabel('${' + component.k + '}')
              )(
                TemplateConfigEntity.createDefaultTemplateConfig(
                  AiComponentIdentifierToComponentIdentifierEnum[
                    component.ex
                  ] as ComponentIdentifierEnum
                )
              )
            : _flow(
                withIdsAndValidationStatus,
                TemplateConfigEntity.withLabel(component.lb)
              )(TemplateConfigEntity.createDefaultTemplateConfig(ComponentIdentifierEnum.TEXT))
        const cellComponent = _flow(
          withIdsAndValidationStatus,
          TemplateConfigEntity.withLabel(component.lb),
          TemplateConfigEntity.withChildren([cellContent])
        )(TemplateConfigEntity.createDefaultTemplateConfig(ComponentIdentifierEnum.TABLE_CELL))

        return cellComponent as TemplateConfigInterface
      } else {
        return {
          ...node,
          templateConfig: children,
        }
      }
    })

// --- Data Model Extraction ---

const extractDataModelItemsFromAiConfig = (
  config: AiTemplateConfig,
  mappedFields: AiDesignerContext['mapped_fields']
): DataModelDraftItem[] => {
  const extractKeysFn = (
    prefix: string,
    config: AiUiTemplateElement
  ): DataModelDraftItem | DataModelDraftItem[] => {
    const newPrefix = config.k ? `${prefix}${prefix ? '.' : ''}${config.k}` : prefix
    const inputDataType =
      AiComponentIdentifierToComponentIdentifierEnum[config.ci] === FormFieldsEnum.INPUT
        ? AiInputTypeToDataTypeMap[config.st]
        : null
    const type =
      inputDataType ?? (AiComponentDataType[config.dt] as unknown as DataModelAttributeType)

    const mappedField = mappedFields.find((field) => field.key === newPrefix)

    const createMappedDataModelDraftItem = (
      config: AiUiTemplateElement,
      mappedField: AiDesignerContext['mapped_fields'][0]
    ): DataModelDraftItem =>
      ({
        label: config.lb,
        templateConfigIdentifier: AiComponentIdentifierToComponentIdentifierEnum[config.ci],
        key: mappedField.data_model_field,
        description: mappedField.dataModelEntity?.description ?? config.ds ?? '',
        type:
          mappedField.dataModelEntity?.type ??
          (AiComponentDataType[config.dt] as unknown as DataModelAttributeType),
      }) as DataModelDraftItem

    const createNewDataModelDraftItem = (config: AiUiTemplateElement): DataModelDraftItem =>
      ({
        label: config.lb,
        key: newPrefix,
        description: config.ds,
        ...(config.dt === 'A'
          ? { type: DataModelAttributeType.STRING, multiInstance: true }
          : {
              type: config.dt === AiComponentDataType.NONE ? DataModelAttributeType.STRING : type,
              multiInstance: false,
            }),
        templateConfigIdentifier: AiComponentIdentifierToComponentIdentifierEnum[config.ci],
      }) as DataModelDraftItem

    return mappedField
      ? createMappedDataModelDraftItem(config, mappedField)
      : createNewDataModelDraftItem(config)
  }

  const filteredComponents =
    config && config.components
      ? config.components.filter(
          (c) => AI_FORM_FIELDS.has(c.ci as AiComponentIdentifier) && c.dt && c.dt.length > 0
        )
      : []
  return _flatten(filteredComponents.map((el: AiUiTemplateElement) => extractKeysFn('', el)))
}

// Resolve a generated item against the PROJECT/LIBRARY catalogue. The agent names the object after a
// catalogue entry; match on label or the last key segment (case-insensitive).
const matchProjectCatalogueEntry = (
  dataModel: DataModelDraftItem,
  catalogue: ProjectDataModelCatalogueEntry[]
): ProjectDataModelCatalogueEntry | undefined => {
  if (!catalogue?.length) {
    return undefined
  }
  const lastKeySegment = dataModel.key?.split('.').pop() ?? ''
  const candidates = [dataModel.label, lastKeySegment]
    .filter(Boolean)
    .map((value) => value.toLowerCase())

  return catalogue.find((entry) => candidates.includes(entry.name?.toLowerCase()))
}

// Promote a matched item into a REFERENCED object: type OBJECT, reference + app info attached.
const toReferencedDataModelItem = (
  dataModel: DataModelDraftItem,
  entry: ProjectDataModelCatalogueEntry
): DataModelDraftItem => ({
  ...dataModel,
  type: DataModelAttributeType.OBJECT,
  state: DataModelItemStatus.REFERENCED,
  targetResourceDefinitionId: entry.resourceDefinitionId,
  appId: entry.appId,
  referenceAppInfo: {
    appId: entry.appId,
    appName: entry.appName,
    isCurrentApp: entry.isCurrentApp,
  },
})

const extractDataModelChangesFromAiConfig = (
  config: AiTemplateConfig,
  mappedFields: AiDesignerContext['mapped_fields'],
  existingDataModelLeaves: { name?: string }[] = [],
  projectDataModelCatalogue: ProjectDataModelCatalogueEntry[] = []
): DataModelDraft | null => {
  // MT-3221: the agent emits OBJECT-level references directly in mapped_fields (isReference +
  // targetResourceDefinitionId/appId, already validated by a backend guard). Trust those instead of
  // re-deriving the catalogue match per UI field — build one REFERENCED item per emitted reference
  // (keyed by the agent's object key, e.g. "client" -> application.client referencing the object),
  // and suppress the UI-derived fields that are that object's attributes so they aren't recreated flat.
  // The agent nests a referenced object's fields under it (e.g. application.client.name); derive the
  // object's full data-model path from those fields so the reference is created at application.client
  // (under the process root object) and not at the top-level root entity.
  const uiItems = extractDataModelItemsFromAiConfig(config, mappedFields)
  const referencePathFor = (objectKey: string): string => {
    const nested = uiItems.find((it) => {
      const parts = (it.key ?? '').split('.')
      return parts.length >= 2 && parts[parts.length - 2] === objectKey
    })
    return nested?.key ? nested.key.split('.').slice(0, -1).join('.') : objectKey
  }
  const agentReferenced: DataModelDraftItem[] = mappedFields
    .filter((mf) => mf.isReference && mf.targetResourceDefinitionId && mf.appId)
    .map((mf) => {
      const entry = projectDataModelCatalogue.find(
        (e) => e.resourceDefinitionId === mf.targetResourceDefinitionId && e.appId === mf.appId
      )
      const fullKey = referencePathFor(mf.key)
      return {
        key: fullKey,
        label: fullKey,
        type: DataModelAttributeType.OBJECT,
        state: DataModelItemStatus.REFERENCED,
        targetResourceDefinitionId: mf.targetResourceDefinitionId,
        appId: mf.appId,
        ...(entry && {
          referenceAppInfo: {
            appId: entry.appId,
            appName: entry.appName,
            isCurrentApp: entry.isCurrentApp,
          },
        }),
      } as DataModelDraftItem
    })
  const agentReferencedKeys = new Set(agentReferenced.map((item) => item.key))
  // attribute names of every referenced object (the form's fields resolve under the reference, so
  // they must NOT be recreated as flat process fields).
  const referencedAttributeNames = new Set(
    agentReferenced.flatMap((ref) => {
      const entry = projectDataModelCatalogue.find(
        (e) => e.resourceDefinitionId === ref.targetResourceDefinitionId && e.appId === ref.appId
      )
      return (entry?.attributes ?? []).map((a) => a.name.toLowerCase())
    })
  )

  const changes = uiItems
    .filter((dataModel) => {
      const last = (dataModel.key?.split('.').pop() ?? '').toLowerCase()
      // drop items already promoted to a reference, or that are an attribute of a referenced object.
      return !agentReferencedKeys.has(dataModel.key) && !referencedAttributeNames.has(last)
    })
    .reduce(
    (filteredDataModels, dataModel) => {
      const mappedField = mappedFields.find(
        (mappedField) => dataModel.key === mappedField.data_model_field
      )

      // Guard against the AI not echoing an already-existing key in mapped_fields: if the key is
      // already a data-model leaf, classify it as existing so we don't submit a duplicate attribute.
      const keyExistsInDataModel = existingDataModelLeaves.some(
        (leaf) => leaf.name === dataModel.key
      )

      const catalogueEntry = matchProjectCatalogueEntry(dataModel, projectDataModelCatalogue)

      // A project/library object reference takes precedence: it can't be a primitive bulk-path key
      // (only OBJECT-reference apply can express it), and it's never a duplicate of a process leaf.
      if (catalogueEntry && !mappedField && !keyExistsInDataModel) {
        filteredDataModels.referenced.push(toReferencedDataModelItem(dataModel, catalogueEntry))
      } else if (mappedField) {
        filteredDataModels.existing.push({
          ...dataModel,
          templateConfigMappedKey: mappedField.key,
        })
      } else if (keyExistsInDataModel) {
        filteredDataModels.existing.push(dataModel)
      } else {
        filteredDataModels.new.push(dataModel)
      }

      return filteredDataModels
    },
    {
      new: [] as DataModelDraftItem[],
      existing: [] as DataModelDraftItem[],
      referenced: [...agentReferenced] as DataModelDraftItem[],
    }
  )
  return changes.new.length || changes.existing.length || changes.referenced.length ? changes : null
}

const extractDataModelUpdateFromTemplateConfig = (
  config: TemplateConfigInterface | TemplateConfigInterface[]
): DataModelDraftUpdateItem[] => {
  if (Array.isArray(config)) {
    return _flatten(config.map((child) => extractDataModelUpdateFromTemplateConfig(child)))
  }

  const { id, key, componentIdentifier, templateConfig } = config

  return _flattenDeep([
    key ? ({ key, id, componentIdentifier } satisfies DataModelDraftUpdateItem) : null,
    templateConfig?.length
      ? templateConfig.map((child) => extractDataModelUpdateFromTemplateConfig(child))
      : null,
  ]).filter(Boolean)
}

const updateDataModelWithTemplateConfigIdentifiers = (
  dataModelChanges: DataModelDraft | null,
  templateConfig: TemplateConfigInterface | TemplateConfigInterface[]
): DataModelDraft => {
  const updates = extractDataModelUpdateFromTemplateConfig(templateConfig)

  function updateDataModelItem(dataModel: DataModelDraftItem): DataModelDraftItem {
    const update = updates.find(
      (update) => update.key === dataModel.key || update.key === dataModel.templateConfigMappedKey
    )
    return update
      ? {
          ...dataModel,
          templateConfigIdentifier: update.componentIdentifier,
          templateConfigId: update.id,
        }
      : dataModel
  }

  return {
    new: dataModelChanges.new.map(updateDataModelItem),
    existing: dataModelChanges.existing.map(updateDataModelItem),
    ...(dataModelChanges.referenced
      ? { referenced: dataModelChanges.referenced.map(updateDataModelItem) }
      : {}),
  }
}

// --- Merge & Bulk Update ---

enum TemplateConfigMergeCase {
  NO_DATA = 'no_data',
  EMPTY_UI = 'empty_ui',
  EMPTY_DRAFT = 'empty-draft',
  MERGE_TEMPLATES = 'merge-templates',
  INSIDE_CONTAINER = 'inside-container',
}

const mergeDraftTemplateConfig = (
  draftTemplateConfig: TemplateConfigInterface,
  templateConfig: TemplateConfigInterface,
  selectedTemplateConfigId?: number
): TemplateConfigInterface => {
  const hasTemplateConfig = !_isEmpty(templateConfig)
  const hasDraftTemplateConfig = !_isEmpty(draftTemplateConfig)

  let mergeCase: TemplateConfigMergeCase | null = null
  if (!hasTemplateConfig && !hasDraftTemplateConfig) {
    mergeCase = TemplateConfigMergeCase.NO_DATA
  } else if (!hasTemplateConfig && hasDraftTemplateConfig) {
    mergeCase = TemplateConfigMergeCase.EMPTY_UI
  } else if (hasTemplateConfig && !hasDraftTemplateConfig) {
    mergeCase = TemplateConfigMergeCase.EMPTY_DRAFT
  } else {
    if (selectedTemplateConfigId && templateConfig.id !== selectedTemplateConfigId) {
      mergeCase = TemplateConfigMergeCase.INSIDE_CONTAINER
    } else {
      mergeCase = TemplateConfigMergeCase.MERGE_TEMPLATES
    }
  }

  switch (mergeCase) {
    case TemplateConfigMergeCase.NO_DATA: {
      return null
    }
    case TemplateConfigMergeCase.EMPTY_UI: {
      return { ...draftTemplateConfig, draft: true }
    }
    case TemplateConfigMergeCase.EMPTY_DRAFT: {
      return templateConfig
    }
    case TemplateConfigMergeCase.INSIDE_CONTAINER: {
      if (templateConfig.id === selectedTemplateConfigId) {
        return {
          ...templateConfig,
          templateConfig: [{ ...draftTemplateConfig, draft: true, order: 0 }],
        }
      } else {
        if (templateConfig.templateConfig?.length) {
          return {
            ...templateConfig,
            templateConfig: templateConfig.templateConfig.map((child) =>
              mergeDraftTemplateConfig(draftTemplateConfig, child, selectedTemplateConfigId)
            ),
          }
        } else {
          return templateConfig
        }
      }
    }
    case TemplateConfigMergeCase.MERGE_TEMPLATES: {
      const lastChildOrder = _last(templateConfig.templateConfig)?.order
      return {
        ...templateConfig,
        templateConfig: [
          ...(templateConfig?.templateConfig || []),
          {
            ...draftTemplateConfig,
            draft: true,
            order: lastChildOrder ? lastChildOrder + 1 : 0,
          },
        ],
      }
    }
  }
}

const getTemplateConfigMergedDisplayOptions = (
  config: Partial<TemplateConfigInterface>
): DisplayOptionsInterface =>
  config.platformsDisplayOptions.reduce(
    (acc, platformOptions) => deepmerge(acc, platformOptions),
    {} as any
  )

const parseTemplateToBulkUpdate = (
  config: TemplateConfigInterface,
  aiContext: AiDesignerContext | Record<string, never> = {}
): BulkUpdatePayload => {
  let aiFields: { k: string; ds?: string; ex?: string }[] | undefined
  try {
    aiFields = JSON.parse(aiContext.answer ?? 'null')?.cs
  } catch {
    aiFields = undefined
  }
  const { key, templateConfig, id, ...validConfigProps } = config

  const templateConfigWithData =
    templateConfig?.map((config) => parseTemplateToBulkUpdate(config, aiContext)) ?? []

  const dataModelProperty = !_isEmpty(key)
    ? {
        name: key,
        description: aiFields?.find((field) => field.k === key)?.ds,
        type: getFormFieldDataModelType(
          validConfigProps.componentIdentifier,
          getTemplateConfigMergedDisplayOptions(validConfigProps)?.flowxProps.inputType
        ),
        exampleValue: aiFields?.find((field) => field.k === key)?.ex,
        useInReporting: false,
        isSensitive: false,
        isArray: ArrayFormFields.includes(validConfigProps.componentIdentifier as FormFieldsEnum),
      }
    : null

  return {
    ...validConfigProps,
    key,
    templateConfigWithData,
    dataModelProperty,
  }
}

const findInMergedConfig = (
  config: TemplateConfigInterface,
  templateConfigId: number
): TemplateConfigInterface => {
  if (config.id === templateConfigId) {
    return config
  } else if (config.templateConfig?.length) {
    return config.templateConfig.find((child) => findInMergedConfig(child, templateConfigId))
  }

  return null
}

const toBulkUpdatePayload = (payload: {
  draftConfig: TemplateConfigInterface[]
  mergedConfig: TemplateConfigInterface
  aiContext?: AiDesignerContext
}): BulkUpdatePayload[] => {
  const { draftConfig, mergedConfig, aiContext } = payload

  return draftConfig.map((config) =>
    parseTemplateToBulkUpdate(
      deepmerge(config, {
        order: findInMergedConfig(mergedConfig, config.id)?.order,
      } as any),
      aiContext
    )
  )
}

// --- Template Config & Data Model Utilities ---

const templateConfigPropertyUpdates = (
  templateConfig: TemplateConfigInterface[],
  textInterpolatingDataModel: DataModelDraftItem[],
  tableSourceDataModel: DataModelDraftItem[]
): TemplateConfigInterface[] => {
  const tableDMByTemplateConfigId = new Map(
    tableSourceDataModel.map((dm) => [dm.templateConfigId, dm])
  )

  const textInterpolatingDMByTemplateConfigId = new Map(
    textInterpolatingDataModel.map((dm) => [dm.templateConfigId, dm])
  )

  const shouldInterpolateLabel = (tc: TemplateConfigInterface): boolean =>
    TableElementsWithDataModelTextInterpolation.includes(
      tc.componentIdentifier as ComponentIdentifierEnum
    )

  return templateConfig.flatMap((tc) => {
    const tableDM = tableDMByTemplateConfigId.get(tc.id)
    if (tableDM) {
      return [TemplateConfigEntity.withTableSource(tableDM.key, tc) as TemplateConfigInterface]
    }
    const textInterpolatingDM = textInterpolatingDMByTemplateConfigId.get(tc.id)
    if (textInterpolatingDM && shouldInterpolateLabel(tc)) {
      return [
        TemplateConfigEntity.withLabel(
          `\${${textInterpolatingDM.key}}`,
          tc
        ) as TemplateConfigInterface,
      ]
    }
    return []
  })
}

// Build the OBJECT-reference attribute params for a referenced (project/library) draft item. The
// reference arm is { targetResourceDefinitionId, appId } (vs { targetFlowxUuid } for process refs).
const buildReferencedAttributeParams = (
  item: DataModelDraftItem
): CreateDataModelAttributeParams => ({
  name: item.key.split('.').pop() ?? item.key,
  type: DataModelAttributeType.OBJECT,
  multiInstance: item.multiInstance ?? false,
  description: item.description ?? '',
  exampleValue: '',
  useInReporting: false,
  isSensitive: false,
  enumReference: null,
  reference: {
    targetResourceDefinitionId: item.targetResourceDefinitionId,
    appId: item.appId,
  },
  properties: null,
})

const prepareDataModelForBulkCreate = (dataModel: DataModelDraftItem[]): DataModelDraftItem[] =>
  dataModel
    .filter((dataModelObj) => !dataModelObj.inactive)
    .map((dataModelObj) =>
      dataModelObj.parentDataModelId
        ? {
            ...dataModelObj,
            key: `${dataModel.find((dm) => dm.id === dataModelObj.parentDataModelId)?.key}.${dataModelObj.key}`,
          }
        : dataModelObj
    )
    .filter((dataModelObj) => dataModelObj.state === DataModelItemStatus.CREATED)

// --- Tree Traversal & Action Mapping ---

function walkInParallel(
  draftNode: TemplateConfigInterface,
  persistedNode: TemplateConfigInterface,
  reducer: (draftNode: TemplateConfigInterface, persistedNode: TemplateConfigInterface) => void
): void {
  if (!draftNode || !persistedNode) {
    return
  }

  reducer(draftNode, persistedNode)

  const draftChildren = draftNode.templateConfig ?? []
  const persistedChildren = persistedNode.templateConfig ?? []

  for (let i = 0; i < draftChildren.length && i < persistedChildren.length; i++) {
    walkInParallel(draftChildren[i], persistedChildren[i], reducer)
  }
}

// TODO: To remove on UI action mappings refactoring w/ BE endpoint
function buildKeyToIdMap(
  draftRoot: TemplateConfigInterface,
  persistedRoot: TemplateConfigInterface
): Record<string | number, number> {
  const map: Record<string | number, number> = {}

  walkInParallel(draftRoot, persistedRoot, (draftNode, persistedNode) => {
    const key = draftNode.key || draftNode.meta?.aiElementKey
    if (key) {
      map[key] = persistedNode.id
    }
  })

  return map
}

function linkUiActionMappings(
  mappings: AiDesignerContext['actionMappings'],
  keyToId: Record<string | number, number>
): AiDesignerContext['actionMappings'] {
  if (!mappings) {
    return mappings
  }

  return mappings.map((btn) => ({
    ...btn,
    templateConfigId: keyToId[btn.button_key] ?? btn.templateConfigId,
  }))
}

// TODO: To remove on UI action mappings refactoring w/ BE endpoint
// -------------------------------------------------------------
//   Build a map { [formId]: { buttonFlowxUuid: string; forms: { id: number; flowxUuid: string }[] } }
//   where formId is the template-config id of a FORM component and the value is the
//   button mapping that belongs to that form.
// -------------------------------------------------------------
function mapTemplateConfigComponentsToAiLinkingElements(
  config: TemplateConfigInterface | TemplateConfigInterface[],
  buttonMappings: AiDesignerContext['actionMappings']
): Record<number, { buttonFlowxUuid: string; forms: { id: number; flowxUuid: string }[] }> {
  // Helper to get mapping object for a templateConfigId
  const getButtonMapping = (templateConfigId: number): AiDesignerContext['actionMappings'][0] =>
    buttonMappings.find((b) => b.templateConfigId === templateConfigId)

  type Element =
    | { type: 'form'; id: number; flowxUuid: string }
    | {
        type: 'button'
        id: number
        flowxUuid: string
        mapping: AiDesignerContext['actionMappings'][0]
        isSubmit: boolean
      }

  const orderedElements: Element[] = []

  // Depth-first traversal preserving natural order
  const traverse = (node: TemplateConfigInterface): void => {
    if (node.componentIdentifier === ComponentIdentifierEnum.FORM) {
      orderedElements.push({ type: 'form', id: node.id, flowxUuid: node.flowxUuid })
    } else if (node.componentIdentifier === ComponentIdentifierEnum.BUTTON) {
      const mapping = getButtonMapping(node.id)
      if (mapping) {
        orderedElements.push({
          type: 'button',
          id: node.id,
          flowxUuid: node.flowxUuid,
          mapping,
          isSubmit: mapping.button_type === UiFlowLinkButtonType.Submit,
        })
      }
    }

    node.templateConfig?.forEach(traverse)
  }

  if (Array.isArray(config)) {
    config.forEach(traverse)
  } else {
    traverse(config)
  }

  // Build the form → buttons map according to the sequential rules
  const result: Record<
    number,
    { buttonFlowxUuid: string; forms: { id: number; flowxUuid: string }[] }
  > = {}
  let currentSubmitButton: { id: number; flowxUuid: string } | null = null
  const pendingForms: { id: number; flowxUuid: string }[] = []

  for (const el of orderedElements) {
    if (el.type === 'button') {
      const btnMapping = el.mapping

      // Ensure button exists in result even if it will have no forms
      if (!result[btnMapping.templateConfigId]) {
        result[btnMapping.templateConfigId] = {
          buttonFlowxUuid: el.flowxUuid,
          forms: [],
        }
      }

      // Cancel (or other) non-submit button does not influence assignment rules
      if (el.isSubmit) {
        // Assign this button to any forms seen before it but not yet assigned
        pendingForms.forEach(({ id: fid, flowxUuid: fUuid }) => {
          if (!result[btnMapping.templateConfigId]) {
            result[btnMapping.templateConfigId] = {
              buttonFlowxUuid: el.flowxUuid,
              forms: [],
            }
          }
          result[btnMapping.templateConfigId].forms.push({ id: fid, flowxUuid: fUuid })
        })
        pendingForms.length = 0 // clear

        // This button becomes the active submit button for subsequent forms
        currentSubmitButton = { id: btnMapping.templateConfigId, flowxUuid: el.flowxUuid }
      }
    } else {
      // element is form
      const { id: fid, flowxUuid: fUuid } = el
      if (currentSubmitButton) {
        if (!result[currentSubmitButton.id]) {
          result[currentSubmitButton.id] = {
            buttonFlowxUuid: currentSubmitButton.flowxUuid,
            forms: [],
          }
        }
        result[currentSubmitButton.id].forms.push({ id: fid, flowxUuid: fUuid })
      } else {
        pendingForms.push({ id: fid, flowxUuid: fUuid })
      }
    }
  }

  return result
}

// --- Export ---

export const DesignerService = {
  AiComponentIdentifierToComponentIdentifierEnum,
  mapAiUiToTemplateConfig: _curry(mapAiUiToTemplateConfig),
  extractDataModelChangesFromAiConfig,
  updateDataModelWithTemplateConfigIdentifiers,
  mergeDraftTemplateConfig,
  toBulkUpdatePayload,
  templateConfigPropertyUpdates,
  prepareDataModelForBulkCreate,
  buildReferencedAttributeParams,
  walkInParallel,
  buildKeyToIdMap,
  linkUiActionMappings,
  mapTemplateConfigComponentsToAiLinkingElements,
}
